import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import TeacherHeader from "../common/TeacherHeader";
import Footer from "../common/Footer";

import { baseUrl } from "../../Utility/constants";
import { ReportCardEndpoints } from "../../Utility/Endpoints";
import { getSchoolId, getCurrentSession } from "../../Utility/commonFunctions";

const SubjectAndTeacherWiseAnnualExamReport = () => {
    const [report, setReport] = useState(null);
    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionDetails, setSessionDetails] = useState("");
    const [teacherData, setTeacherDData] = useState([]);
    const [exporting, setExporting] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        fetchReport();
    }, []);

    const exportToPdf = async () => {
        if (!reportRef.current) return;

        try {
            setExporting(true);

            // Wait for React to apply export styles
            await new Promise(resolve => requestAnimationFrame(resolve));

            const input = reportRef.current;

            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                scrollX: 0,
                scrollY: 0,
                windowWidth: input.scrollWidth,
                windowHeight: input.scrollHeight
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "pt",
                format: "a4"
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 18;

            const imgWidth = pageWidth - margin * 2;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(
                imgData,
                "PNG",
                margin,
                margin,
                imgWidth,
                Math.min(imgHeight, pageHeight - margin * 2),
                undefined,
                "FAST"
            );

            pdf.save(
                `annual-exam-report_${sessionDetails}.pdf`
            );
        } catch (error) {
            console.error("PDF export failed:", error);
            toast.error("Failed to export PDF.");
        } finally {
            setExporting(false);
        }
    };

    const fetchReport = async () => {
        try {
            setLoading(true);

            const teacherId = localStorage.getItem("userID");

            const url = `${baseUrl}/api/Teachers/GetTeacherDetailsByTeacherId?teacherId=${teacherId}`;
            const teacherResponse = await axios.get(url);

            if (
                teacherResponse &&
                teacherResponse.data &&
                teacherResponse.data.length > 0
            ) {
                setTeacherDData(teacherResponse?.data?.[0]);
            } else {
                setTeacherDData([]);
            }

            const reportResponse = await axios.get(
                `${ReportCardEndpoints.SubjectAndTeacherwiseAnnualExamReportByTeacherId}?teacherId=${teacherId}`
            );

            const schoolId = await getSchoolId();
            const schoolResponse = await axios.get(
                `${baseUrl}/api/SchoolGeneralInfo/GetSchoolInfo?schoolId=${schoolId}`
            );

            const sessionName = await getCurrentSession();

            setSessionDetails(sessionName);
            setReport(reportResponse.data);
            setSchool(schoolResponse.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load Overall Annual Exam Report.");
        } finally {
            setLoading(false);
        }
    };

    const thStyle = {
        border: "2px solid black",
        background: "#fff",
        fontWeight: "bold",
        verticalAlign: "middle",
        whiteSpace: "normal",
        padding: "6px"
    };

    const tdStyle = {
        border: "1px solid black",
        padding: "4px"
    };

   const verticalHeaderStyle = {
    border: "2px solid black",
    background: "#fff",
    width: "28px",
    minWidth: "28px",
    maxWidth: "28px",
    height: "140px",
    padding: 0,
    textAlign: "center",
    verticalAlign: "middle",

    // Needed for PDF absolute positioning
    position: "relative",
    overflow: "hidden"
};

   const verticalHeaderLabelStyle = exporting
   ? {
      // PDF Export (CENTER ALIGNED)
      position: "absolute",
      top: "50%",
      left: "50%",

      // Center first, then rotate
      transform: "translate(-50%, -50%) rotate(-90deg)",
      transformOrigin: "center center",

      whiteSpace: "nowrap",
      fontFamily: "Times New Roman, serif",
      fontWeight: "bold",
      fontSize: "11px",
      lineHeight: 1,
      textAlign: "center"
    }
  : {
      // Browser UI (leave unchanged)
      writingMode: "vertical-rl",
      textOrientation: "mixed",
      transform: "rotate(180deg)",
      whiteSpace: "nowrap",
      fontFamily: "Times New Roman, serif",
      fontWeight: "bold",
      fontSize: "11px",
      lineHeight: 1
    };

    return (
        <>
            <TeacherHeader />

            <div className="container my-4">
                <div className="d-flex justify-content-end mb-3">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={exportToPdf}
                        disabled={loading || exporting || !report}
                    >
                        {exporting ? "Exporting..." : "Export to PDF"}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary"></div>
                    </div>
                ) : (
                    <div
                        ref={reportRef}
                        className="bg-white border border-dark p-4"
                        style={{
                            maxWidth: "1400px",
                            width: "100%",
                            margin: "0 auto",
                            fontFamily: "Times New Roman, serif",
                            color: "#000"
                        }}
                    >
                        <table className="table table-bordered border-dark mb-0">
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            width: "140px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                        }}
                                    >
                                        <img
                                            src={school?.logo}
                                            alt="School Logo"
                                            style={{
                                                width: "100px",
                                                height: "100px",
                                                objectFit: "contain"
                                            }}
                                        />
                                    </td>

                                    <td
                                        className="text-center border-start-0 border-end-0"
                                        style={{ verticalAlign: "middle" }}
                                    >
                                        <h6 className="mb-1 fw-bold">
                                            {school?.organizationName}
                                        </h6>

                                        <h3 className="fw-bold mb-1 text-uppercase">
                                            {school?.schoolOrCollegeName}
                                        </h3>

                                        <div className="fw-semibold mb-1">
                                            (Affiliated to H.S.C. Board, Govt. of Maharashtra)
                                        </div>

                                        <div className="small">
                                            Address : {school?.address}
                                        </div>

                                        <div className="small">
                                            Phone :{" "}
                                            <strong>{school?.schoolOrCollegeContactNo}</strong>
                                            {" | "}
                                            Website :{" "}
                                            <strong>{school?.schoolWebsiteName}</strong>
                                        </div>

                                        <div className="small">
                                            Email : <strong>{school?.email}</strong>
                                        </div>

                                        <div className="small">
                                            U-DISE No : <strong>{school?.udiseNo}</strong>

                                            {(school?.ssC10thIndexNo ||
                                                school?.hsC12thIndexNo) && (
                                                    <>
                                                        {" | "}Index No :{" "}
                                                        <strong>
                                                            {school?.ssC10thIndexNo}
                                                            {school?.ssC10thIndexNo &&
                                                                school?.hsC12thIndexNo &&
                                                                " | "}
                                                            {school?.hsC12thIndexNo}
                                                        </strong>
                                                    </>
                                                )}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="text-center mb-3">
                            <h5 className="fw-bold">
                                Subject & Teacherwise Result of Annual Exam ({sessionDetails})
                            </h5>
                        </div>

                        <div className="row fw-bold mb-3">
                            <div className="col-8">
                                Class :- {report?.standardName}
                                {"   "}
                                Division :- {report?.sectionName}
                            </div>

                            <div className="col-4 text-end">
                                Class Teacher :- {report?.classTeacherName}
                            </div>
                        </div>

                        <div
                            className="table-responsive mb-3"
                            style={{
                                overflowX: "auto",
                                overflowY: "hidden",
                                border: "2px solid black"
                            }}
                        >
                            <table
                                className="table table-bordered text-center align-middle mb-0"
                                style={{
                                    borderCollapse: "collapse",
                                    tableLayout: "fixed",
                                    fontSize: "12px",
                                    fontFamily: "Times New Roman, serif"
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th rowSpan="2" style={{ ...thStyle, width: "20px" }}>
                                            Sr.<br /> No
                                        </th>

                                        <th
                                            rowSpan="2"
                                            style={{
                                                ...tdStyle,
                                                textAlign: "left",
                                                paddingLeft: "8px",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                verticalAlign: "middle",
                                                width: "45px"
                                            }}
                                        >
                                            Subject
                                        </th>

                                        {[
                                            "Pass",
                                            "Promoted",
                                            "Total Pass",
                                            "Failed",
                                            "Absent",
                                            "Total",
                                            "Percentage of Result"
                                        ].map((title) => (
                                            <th key={title} rowSpan="2" style={verticalHeaderStyle}>
                                                <span style={verticalHeaderLabelStyle}>{title}</span>
                                            </th>
                                        ))}

                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>Above 90%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>80% To 89%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>70% To 79%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>60% To 69%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>50% To 59%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>40% To 49%</span>
                                        </th>
                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>35% To 39%</span>
                                        </th>

                                        <th rowSpan="2" style={verticalHeaderStyle}>
                                            <span style={verticalHeaderLabelStyle}>Top Score In Subject</span>
                                        </th>

                                        <th
                                            rowSpan="2"
                                            style={{
                                                ...thStyle,
                                                minWidth: "200px",
                                                width: "100px"
                                            }}
                                        >
                                            Name of The Highest Scorer
                                        </th>

                                        <th
                                            rowSpan="2"
                                            style={{
                                                ...thStyle,
                                                minWidth: "200px",
                                                width: "100px"
                                            }}
                                        >
                                            Name of Subject Teacher
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {report?.subjects?.map((item, index) => (
                                        <tr key={item.subjectId}>
                                            <td style={tdStyle}>{index + 1}</td>

                                            <td style={{ ...tdStyle, textAlign: "left", paddingLeft: "8px" }}>
                                                {item.subjectName}
                                            </td>

                                            <td style={tdStyle}>{item.pass}</td>
                                            <td style={tdStyle}>{item.promoted}</td>
                                            <td style={tdStyle}>{item.totalPass}</td>
                                            <td style={tdStyle}>{item.failed}</td>
                                            <td style={tdStyle}>{item.absent}</td>
                                            <td style={tdStyle}>{item.totalStudents}</td>

                                            <td style={tdStyle}>
                                                {Number(item.resultPercentage).toFixed(2)}
                                            </td>

                                            <td style={tdStyle}>{item.above90}</td>
                                            <td style={tdStyle}>{item.between80To89}</td>
                                            <td style={tdStyle}>{item.between70To79}</td>
                                            <td style={tdStyle}>{item.between60To69}</td>
                                            <td style={tdStyle}>{item.between50To59}</td>
                                            <td style={tdStyle}>{item.between40To49}</td>
                                            <td style={tdStyle}>{item.between35To39}</td>

                                            <td style={tdStyle}>{item.topScore}</td>

                                            <td style={{ ...tdStyle, textAlign: "left", paddingLeft: "8px" }}>
                                                {item.highestScorer}
                                            </td>

                                            <td style={{ ...tdStyle, textAlign: "left", paddingLeft: "8px" }}>
                                                {item.subjectTeacher}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="small fw-semibold mt-1">
                            * Filled By Class Teacher
                        </div>

                        <div className="row text-center">
                            <div className="col-3">
                                <div style={{ height: "50px" }}></div>
                                <div className="fw-semibold">Checked By</div>
                            </div>

                            <div className="col-3">
                                <div style={{ height: "50px" }}></div>
                                <div>Class Teacher</div>
                            </div>

                            <div className="col-3">
                                <div style={{ height: "50px" }}></div>
                                <div className="fw-semibold">Vice-Principal</div>
                            </div>

                            <div className="col-3">
                                <div style={{ height: "50px" }}></div>
                                <div className="fw-semibold">Principal</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ToastContainer position="top-right" autoClose={3000} />
            <Footer />
        </>
    );
};

export default SubjectAndTeacherWiseAnnualExamReport;